/**
 * Carga temprana de Google Maps (Places) + markerclusterer.
 * Debe resolverse ANTES de bootstrapApplication para que la directiva y tec-map
 * encuentren el global `google` listo (mismo efecto que scripts en index.html).
 */

declare global {
	interface Window {
		google?: typeof google;
		markerClusterer?: unknown;
		/** Callback oficial de Google cuando la key/referer/billing fallan. */
		gm_authFailure?: () => void;
	}
}

const MAPS_SCRIPT_ID = 'google-maps-js-api';
const CLUSTERER_SCRIPT_ID = 'google-maps-markerclusterer';

function injectScript(id: string, src: string): Promise<void> {
	const existing = document.getElementById(id) as HTMLScriptElement | null;
	if (existing) {
		if ((existing as HTMLScriptElement & {_loaded?: boolean})._loaded || (id === MAPS_SCRIPT_ID && window.google?.maps?.places)) {
			return Promise.resolve();
		}
		return new Promise((resolve, reject) => {
			existing.addEventListener('load', () => resolve(), {once: true});
			existing.addEventListener('error', () => reject(new Error(`Error cargando script ${id}`)), {once: true});
		});
	}

	return new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.id = id;
		script.src = src;
		script.async = true;
		script.defer = true;
		script.onload = () => {
			(script as HTMLScriptElement & {_loaded?: boolean})._loaded = true;
			resolve();
		};
		script.onerror = () => reject(new Error(`Error cargando script ${id}`));
		document.head.appendChild(script);
	});
}

function waitForPlaces(timeoutMs = 15000): Promise<void> {
	if (typeof google !== 'undefined' && google.maps?.places) {
		return Promise.resolve();
	}
	return new Promise((resolve, reject) => {
		const started = Date.now();
		const timer = setInterval(() => {
			if (typeof google !== 'undefined' && google.maps?.places) {
				clearInterval(timer);
				resolve();
				return;
			}
			if (Date.now() - started > timeoutMs) {
				clearInterval(timer);
				reject(new Error('Timeout esperando Google Maps Places'));
			}
		}, 50);
	});
}

/**
 * Si la key falla, Google pone `gm-err-autocomplete` + background con iconos "!" repetidos
 * y a menudo `disabled`. Hay que dejar el input como campo de texto normal (Shadow DOM incluido).
 */
function scrubMapsErrorFromInput(input: HTMLInputElement): void {
	input.disabled = false;
	input.removeAttribute('disabled');
	input.classList.remove('gm-err-autocomplete', 'pac-target-input');
	input.style.removeProperty('background');
	input.style.removeProperty('background-image');
	input.style.removeProperty('background-repeat');
	input.style.removeProperty('background-position');
	input.style.removeProperty('background-size');
	input.style.removeProperty('padding-left');
}

function unlockGoogleMapsAddressInputs(root: ParentNode = document): void {
	root.querySelectorAll('input.gm-err-autocomplete, input.pac-target-input').forEach((node) => {
		scrubMapsErrorFromInput(node as HTMLInputElement);
	});
	root.querySelectorAll('*').forEach((el) => {
		const sr = (el as HTMLElement).shadowRoot;
		if (sr) {
			unlockGoogleMapsAddressInputs(sr);
		}
	});
}

let mapsAuthFailed = false;
let mapsErrorCleanupTimer: ReturnType<typeof setInterval> | null = null;

function startMapsErrorCleanupWatch(): void {
	if (mapsErrorCleanupTimer) {
		return;
	}
	// Google re-aplica clase/estilos tras el callback; barrer un rato (Shadow DOM del widget).
	mapsErrorCleanupTimer = setInterval(() => {
		if (!mapsAuthFailed) {
			return;
		}
		unlockGoogleMapsAddressInputs();
	}, 200);
	setTimeout(() => {
		if (mapsErrorCleanupTimer) {
			clearInterval(mapsErrorCleanupTimer);
			mapsErrorCleanupTimer = null;
		}
	}, 15000);
}

function registerGoogleMapsAuthFailureHandler(): void {
	if (typeof window === 'undefined') {
		return;
	}
	const prev = window.gm_authFailure;
	window.gm_authFailure = () => {
		mapsAuthFailed = true;
		try {
			prev?.();
		} catch {
			/* ignore */
		}
		unlockGoogleMapsAddressInputs();
		startMapsErrorCleanupWatch();
		[0, 50, 200, 500, 1000, 2000].forEach((ms) => {
			setTimeout(() => unlockGoogleMapsAddressInputs(), ms);
		});
	};
}

/** True si Google rechazó la key (referer/billing). Places no debe usarse. */
export function isGoogleMapsAuthFailed(): boolean {
	return mapsAuthFailed;
}

/**
 * @param apiKey Key de Google Maps JS. Si está vacía, no carga nada (Places/mapa opcionales).
 * Si la key falla (referer, billing, red), no lanza: la UI de dirección sigue usable a mano.
 */
export async function loadGoogleMaps(apiKey: string): Promise<void> {
	if (!apiKey?.trim()) {
		return;
	}

	registerGoogleMapsAuthFailureHandler();

	try {
		if (typeof google !== 'undefined' && google.maps?.places) {
			// Clusterer puede faltar aunque Maps ya esté (p.ej. HMR)
			if (!document.getElementById(CLUSTERER_SCRIPT_ID)) {
				await injectScript(
					CLUSTERER_SCRIPT_ID,
					'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js',
				);
			}
			return;
		}

		await injectScript(
			MAPS_SCRIPT_ID,
			`https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey.trim())}&libraries=places`,
		);
		await waitForPlaces();
		await injectScript(
			CLUSTERER_SCRIPT_ID,
			'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js',
		);
	} catch {
		unlockGoogleMapsAddressInputs();
		return;
	}
}
